import { useRef, useMemo } from 'react';
import { useImmer, immerable } from '@/app/lib/system/immer-wrapper'
// mine:
import { GameMode } from '@/app/lib/game-state/game-types';
import { UserHistory } from '@/app/lib/ui/user-history';
import { Path } from '@/app/lib/routing/path';

const streakFeedbackTimeout = 3000;

class UIState {
    [immerable] = true
    isLoaded: Record<GameMode, boolean> = {
        pyramid: false,
        story: false
    }
    userHistory: UserHistory = new UserHistory();
    isTouch: boolean = false;
    streakFeedback: "happy" | "sad" | null = null;
    originalWordHint: {
        wordIndex: number,
        letterPosition: number
    } | null = null;
    path: Path = '/';
    debugMode: boolean = false;
    // used to help manage animations:
    gameBoxKey: string = '/';
    wordBoxAnimations: {
        reject: Array<() => void>,
        accept: Array<() => void>,
    } = {
        reject: [],
        accept: [],
    }
    streakAnimations: {
        increment: () => void,
        reset: () => void,
        feedback: (feedback: UIState['streakFeedback']) => void
    } = {
        increment: () => {},
        reset: () => {},
        feedback: () => {}
    }
    // autofocus is handled by an effect so it can happen after state changes (see setup)
    // we increment this to request an autofocus
    autoFocusRequestSignal: number = 0;

    setLoaded(gameName: GameMode, value: boolean) {
        this.isLoaded[gameName] = value;
    }
}

export function useUI() {
    const [state, updateState] = useImmer<UIState>(() => new UIState());

    const letterRefs = useRef<(HTMLInputElement | HTMLDivElement | null)[][]>([]);
    const ingredientRefs = useRef<(HTMLDivElement | null)[]>([]);


    // FUNCTIONS MANAGING STATE
    // ------------------------
    const stateActions = useMemo(() => ({
        showOriginalWordHint: (wordIndex: number, letterPosition: number) => {
            updateState(state => {
                state.originalWordHint = {wordIndex, letterPosition};
            });
        },
        hideOriginalWordHint: () => {
            updateState(state => {
                state.originalWordHint = null;
            });
        },
        setMobile: (value: boolean) => {
            updateState(state => {
                state.isTouch = value;
            });
        },
        setLoaded: (gameName: GameMode, value: boolean) => {
            updateState(state => {
                state.setLoaded(gameName, value);
            });
        },
        setStreak: (count: number) => {
            updateState(state => {
                state.userHistory.streakCount = count;
                state.streakFeedback = null;
            })
        },
        resetStreak: (feedback: UIState['streakFeedback']) => {
            updateState(state => {
                state.userHistory.streakCount = 0;
                state.streakFeedback = feedback;
            })
            if (feedback === 'sad') {
                state.streakAnimations.reset();
                state.streakAnimations.feedback('sad');
            }
        },
        incrementStreak: () => {
            updateState(state => {
                state.userHistory.streakCount += 1;
                state.streakFeedback = 'happy';
            })
            state.streakAnimations.increment();
            state.streakAnimations.feedback('happy');
        },
        setStreakFeedback: (feedback: UIState['streakFeedback']) => {
            updateState(state => {
                state.streakFeedback = feedback;
            });
            state.streakAnimations.feedback(feedback);
        },
        setPath(page: Path) {
            updateState(state => {
                state.path = page;
            })
        },
        logPyramidVictory: () => {
            updateState(state => {
                state.userHistory.lifetimePyramidSolves += 1;
            });
        },
        logVisit: (gameMode: GameMode) => {
            updateState(state => {
                state.userHistory.hasVisited[gameMode] = true;
            })
        },
        importHistory: (userHistory: UserHistory) => {
            updateState(state => {
                state.userHistory = userHistory;
            })
        },
        requestAutoFocus: () => {
            updateState(state => {
               state.autoFocusRequestSignal++;
            })
        },
        setDebugMode: (value: boolean) => {
            updateState(state => {
                state.debugMode = value;
            })
        },
        setGameBoxKey: (key: string) => {
            updateState(state => {
                state.gameBoxKey = key;
            })
        },
        requestGameBoxAnimation: () => {
            updateState(state => {
                state.gameBoxKey += '#';
            })
        },
        setRejectAnimation: (
            wordIndex: number,
            rejectionAnimation: () => void
        ) => {
            updateState(state => {
                state.wordBoxAnimations.reject[wordIndex]
                    = rejectionAnimation;
            })
        },
        setAcceptAnimation: (
            wordIndex: number,
            acceptAnimation: () => void
        ) => {
            updateState(state => {
                state.wordBoxAnimations.accept[wordIndex]
                    = acceptAnimation;
            })
        },
        solveAnimation: (wordIndex: number) => {
            state.wordBoxAnimations.accept[wordIndex]?.();
        },
        rejectAnimation: (wordIndex: number) => {
            state.wordBoxAnimations.reject[wordIndex]?.();
        }
    }), [
        updateState,
        state.streakAnimations,
        state.wordBoxAnimations
    ]);


    // FUNCTIONS MANAGING REFS:
    // -----------------------
    const refActions = useMemo(() => ({
        wipeRefs: () => {
            // clear in place
            letterRefs.current.length = 0;
            ingredientRefs.current.length = 0;
        },
        setLetterRef: (
            wordIndex: number,
            letterPosition: number,
            el: (HTMLInputElement | HTMLDivElement | null)
        ) => {
            // because it's a 2d array, we need to make sure the inner array has been initialized
            if (!letterRefs.current[wordIndex]) {
                letterRefs.current[wordIndex] = [];
            }
            letterRefs.current[wordIndex][letterPosition] = el;
        },
        setIngredientRef: (
            position: number,
            el: (HTMLDivElement | null)
        ) => {
            ingredientRefs.current[position] = el;
        },
        focus: (
            position: {
                wordIndex: number,
                letterPosition: number}
        ) => {
            const element = letterRefs.current[position.wordIndex]?.[position.letterPosition];
            if (element) {
                element.focus();
            }
            if (element instanceof HTMLInputElement) {
                setTimeout(() => {
                    element.select();
                }, 0);
            }
        },
        defocusAll: () => {
            setTimeout(() => {
                for (const wordBox of letterRefs.current) {
                    if (!wordBox)
                        continue;
                    for (const letterBox of wordBox) {
                        letterBox?.blur();
                    }
                }
            }, 0);
        }
    }), []);

    return useMemo(() => ({
        ...state,
        ...stateActions,
        ...refActions,
        letterRefs,
        ingredientRefs,
        updateState
    }), [state, stateActions, refActions, letterRefs, ingredientRefs, updateState])   
}

// return type alias
type UI = ReturnType<typeof useUI>;
export default UI;